import type { Api, PurchaseRecord, Session } from "../api/client.ts";
import { ApiError } from "../api/client.ts";
import { daysRemaining, planLabel } from "../plan.ts";
import type { PaywallDialog } from "./paywallDialog.ts";

/**
 * The signed-in user's own account panel: edit name/company, change
 * password, and see the plan summary with a box to redeem a code.
 *
 * Same hand-built-overlay approach as AuthDialog/PaywallDialog/AdminDialog,
 * for the same reason — see authDialog.ts's comment on why a native
 * `<dialog>` is avoided here.
 */
export class ProfileDialog {
  private readonly overlay: HTMLElement;
  private readonly card: HTMLElement;

  private readonly emailText: HTMLInputElement;
  private readonly firstNameInput: HTMLInputElement;
  private readonly lastNameInput: HTMLInputElement;
  private readonly companyInput: HTMLInputElement;
  private readonly saveButton: HTMLButtonElement;
  private readonly profileNotice: HTMLElement;

  private readonly currentPasswordInput: HTMLInputElement;
  private readonly newPasswordInput: HTMLInputElement;
  private readonly confirmPasswordInput: HTMLInputElement;
  private readonly changePasswordButton: HTMLButtonElement;
  private readonly passwordNotice: HTMLElement;

  private readonly planText: HTMLElement;
  private readonly purchasedText: HTMLElement;
  private readonly expiresDateText: HTMLElement;
  private readonly expiresDaysLeftText: HTMLElement;
  private readonly redeemInput: HTMLInputElement;
  private readonly redeemButton: HTMLButtonElement;
  private readonly redeemNotice: HTMLElement;
  private readonly extendPlanButton: HTMLButtonElement;

  private readonly historyEmpty: HTMLElement;
  private readonly historyTable: HTMLTableElement;
  private readonly historyBody: HTMLElement;
  private readonly historyNotice: HTMLElement;

  private resolve: (() => void) | null = null;
  private previouslyFocused: HTMLElement | null = null;
  private currentSession: Session | null = null;

  constructor(
    private readonly api: Api,
    private readonly paywall: PaywallDialog,
    private readonly onSessionUpdated: (session: Session) => void,
    private readonly root: Document = document,
  ) {
    this.overlay = root.createElement("div");
    this.overlay.className = "profile-overlay";
    this.overlay.hidden = true;
    this.overlay.innerHTML = `
      <div class="profile" role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <button type="button" class="profile__close" aria-label="Close">&times;</button>
        <h2 class="profile__title" id="profile-title">Your account</h2>

        <div class="profile__top">
          <section class="profile__section">
            <h3 class="profile__subtitle">Your account</h3>
            <label class="profile__field">
              <span>Email</span>
              <input type="email" class="profile__email" disabled />
            </label>
            <div class="profile__field-row">
              <label class="profile__field">
                <span>First name</span>
                <input type="text" autocomplete="given-name" />
              </label>
              <label class="profile__field">
                <span>Last name</span>
                <input type="text" autocomplete="family-name" />
              </label>
            </div>
            <label class="profile__field">
              <span>Company</span>
              <input type="text" autocomplete="organization" />
            </label>
            <button type="button" class="btn btn--primary profile__save">Save</button>
            <p class="profile__notice profile__notice--profile" hidden></p>
          </section>

          <section class="profile__section">
            <h3 class="profile__subtitle">Change password</h3>
            <div class="profile__field-row">
              <label class="profile__field">
                <span>Current password</span>
                <input type="password" autocomplete="current-password" />
              </label>
            </div>
            <div class="profile__field-row">
              <label class="profile__field">
                <span>New password</span>
                <input type="password" autocomplete="new-password" />
              </label>
              <label class="profile__field">
                <span>Confirm new password</span>
                <input type="password" autocomplete="new-password" />
              </label>
            </div>
            <button type="button" class="btn profile__change-password">Change password</button>
            <p class="profile__notice profile__notice--password" hidden></p>
          </section>
        </div>

        <div class="profile__top">
          <section class="profile__section">
            <dl class="profile-summary">
              <div><dt>Plan</dt><dd class="profile-summary__plan"></dd></div>
              <div><dt>Purchased</dt><dd class="profile-summary__purchased"></dd></div>
              <div><dt>Expires</dt><dd class="profile-summary__expires"><span class="profile-summary__expires-date"></span><span class="profile-summary__days-left" hidden></span></dd></div>
            </dl>
            <label class="profile__field">
              <span>Redeem a code</span>
              <input type="text" autocomplete="off" spellcheck="false" placeholder="Paste a redemption code" />
            </label>
            <button type="button" class="btn profile__redeem">Apply code</button>
            <p class="profile__notice profile__notice--redeem" hidden></p>
          </section>

          <section class="profile__section">
            <h3 class="profile__subtitle">Extend Plan</h3>
            <p class="profile__hint">Buy more time or switch tiers.</p>
            <button type="button" class="btn btn--primary profile__extend-plan">View plans</button>
          </section>
        </div>

        <section class="profile__section">
          <h3 class="profile__subtitle">Purchase History</h3>
          <div class="profile-history">
            <div class="profile-history__empty">No purchases yet.</div>
            <table class="profile-history__table" hidden>
              <thead>
                <tr><th>Date</th><th>Period</th><th>Amount</th><th></th></tr>
              </thead>
              <tbody></tbody>
            </table>
            <p class="profile__notice profile__notice--history" hidden></p>
          </div>
        </section>
      </div>
    `;
    root.body.append(this.overlay);

    this.card = this.overlay.querySelector(".profile")!;

    const fields = this.overlay.querySelectorAll<HTMLInputElement>(".profile__field input");
    this.emailText = this.overlay.querySelector(".profile__email")!;
    this.firstNameInput = fields[1]!;
    this.lastNameInput = fields[2]!;
    this.companyInput = fields[3]!;
    this.saveButton = this.overlay.querySelector(".profile__save")!;
    this.profileNotice = this.overlay.querySelector(".profile__notice--profile")!;

    this.currentPasswordInput = fields[4]!;
    this.newPasswordInput = fields[5]!;
    this.confirmPasswordInput = fields[6]!;
    this.changePasswordButton = this.overlay.querySelector(".profile__change-password")!;
    this.passwordNotice = this.overlay.querySelector(".profile__notice--password")!;

    this.planText = this.overlay.querySelector(".profile-summary__plan")!;
    this.purchasedText = this.overlay.querySelector(".profile-summary__purchased")!;
    this.expiresDateText = this.overlay.querySelector(".profile-summary__expires-date")!;
    this.expiresDaysLeftText = this.overlay.querySelector(".profile-summary__days-left")!;
    this.redeemInput = fields[7]!;
    this.redeemButton = this.overlay.querySelector(".profile__redeem")!;
    this.redeemNotice = this.overlay.querySelector(".profile__notice--redeem")!;
    this.extendPlanButton = this.overlay.querySelector(".profile__extend-plan")!;

    this.historyEmpty = this.overlay.querySelector(".profile-history__empty")!;
    this.historyTable = this.overlay.querySelector(".profile-history__table")!;
    this.historyBody = this.historyTable.querySelector("tbody")!;
    this.historyNotice = this.overlay.querySelector(".profile__notice--history")!;

    this.overlay.querySelector(".profile__close")!.addEventListener("click", () => this.close());
    this.saveButton.addEventListener("click", () => void this.saveProfile());
    this.changePasswordButton.addEventListener("click", () => void this.changePassword());
    this.redeemButton.addEventListener("click", () => void this.redeemCode());
    this.extendPlanButton.addEventListener("click", () => void this.extendPlan());

    this.overlay.addEventListener("mousedown", (event) => {
      if (event.target === this.overlay) this.close();
    });
  }

  /** Open the panel for the given session. Resolves once it's dismissed. */
  open(session: Session): Promise<void> {
    this.fillFrom(session);
    void this.loadPurchases();

    this.previouslyFocused = this.root.activeElement as HTMLElement | null;
    this.setBackgroundInert(true);
    this.overlay.hidden = false;
    this.root.addEventListener("keydown", this.handleKeydown, true);
    this.firstNameInput.focus();

    return new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  private fillFrom(session: Session): void {
    this.currentSession = session;
    this.emailText.value = session.email ?? "";
    this.firstNameInput.value = session.firstName ?? "";
    this.lastNameInput.value = session.lastName ?? "";
    this.companyInput.value = session.company ?? "";
    this.showProfileNotice(null);

    this.currentPasswordInput.value = "";
    this.newPasswordInput.value = "";
    this.confirmPasswordInput.value = "";
    this.showPasswordNotice(null);

    this.planText.textContent = planLabel(session.plan);
    this.purchasedText.textContent = formatPlainDate(session.planStartedAt);
    this.setExpiry(session.planExpiresAt);
    this.redeemInput.value = "";
    this.showRedeemNotice(null);
  }

  /** The expiry date, plus a smaller "(N days left)" alongside it while there's time left on the plan. */
  private setExpiry(planExpiresAt: string | null | undefined): void {
    this.expiresDateText.textContent = formatPlainDate(planExpiresAt);

    const days = planExpiresAt != null ? daysRemaining(planExpiresAt) : null;
    const showsDaysLeft = days !== null && days > 0;
    if (showsDaysLeft) {
      this.expiresDaysLeftText.textContent = days === 1 ? " (1 day left)" : ` (${days} days left)`;
    }
    this.expiresDaysLeftText.hidden = !showsDaysLeft;
  }

  private async saveProfile(): Promise<void> {
    const firstName = this.firstNameInput.value.trim();
    const lastName = this.lastNameInput.value.trim();
    const company = this.companyInput.value.trim();

    this.saveButton.disabled = true;
    this.showProfileNotice(null);
    try {
      const session = await this.api.updateProfile(firstName, lastName, company);
      this.onSessionUpdated(session);
      this.fillFrom(session);
      this.showProfileNotice("Saved.");
    } catch (error) {
      this.showProfileNotice(
        error instanceof ApiError ? error.message : "Could not reach the server. Try again.",
      );
    } finally {
      this.saveButton.disabled = false;
    }
  }

  private async changePassword(): Promise<void> {
    const currentPassword = this.currentPasswordInput.value;
    const newPassword = this.newPasswordInput.value;
    const confirmPassword = this.confirmPasswordInput.value;

    if (newPassword !== confirmPassword) {
      this.showPasswordNotice("The new password fields do not match.");
      return;
    }

    this.changePasswordButton.disabled = true;
    this.showPasswordNotice(null);
    try {
      await this.api.changePassword(currentPassword, newPassword);
      this.currentPasswordInput.value = "";
      this.newPasswordInput.value = "";
      this.confirmPasswordInput.value = "";
      this.showPasswordNotice("Password changed.");
    } catch (error) {
      this.showPasswordNotice(
        error instanceof ApiError ? error.message : "Could not reach the server. Try again.",
      );
    } finally {
      this.changePasswordButton.disabled = false;
    }
  }

  private async redeemCode(): Promise<void> {
    const code = this.redeemInput.value.trim();
    if (code === "") {
      this.showRedeemNotice("Enter a code first.");
      return;
    }

    this.redeemButton.disabled = true;
    this.showRedeemNotice(null);
    try {
      await this.api.redeem(code);
      // Re-fetch rather than trust the redeem response alone, so the summary
      // reflects the same shape of Session everything else in the app works with.
      const session = await this.api.session();
      this.onSessionUpdated(session);
      this.planText.textContent = planLabel(session.plan);
      this.purchasedText.textContent = formatPlainDate(session.planStartedAt);
      this.setExpiry(session.planExpiresAt);
      this.redeemInput.value = "";
      this.showRedeemNotice("Code redeemed. Your plan has been updated.");
    } catch (error) {
      this.showRedeemNotice(
        error instanceof ApiError ? error.message : "Could not reach the server. Try again.",
      );
    } finally {
      this.redeemButton.disabled = false;
    }
  }

  /** Close this dialog and hand off to the paywall's own tier/payment flow. */
  private async extendPlan(): Promise<void> {
    const session = this.currentSession;
    this.close();
    const redeemed = await this.paywall.open(session);
    if (redeemed !== null) this.onSessionUpdated(redeemed);
  }

  private async loadPurchases(): Promise<void> {
    this.historyNotice.hidden = true;
    try {
      const { purchases } = await this.api.purchases();
      this.renderHistory(purchases);
    } catch (error) {
      this.historyEmpty.hidden = true;
      this.historyTable.hidden = true;
      this.historyNotice.textContent =
        error instanceof ApiError ? error.message : "Could not load purchase history.";
      this.historyNotice.hidden = false;
    }
  }

  private renderHistory(purchases: readonly PurchaseRecord[]): void {
    this.historyBody.replaceChildren();

    if (purchases.length === 0) {
      this.historyEmpty.hidden = false;
      this.historyTable.hidden = true;
      return;
    }

    this.historyEmpty.hidden = true;
    this.historyTable.hidden = false;

    for (const purchase of purchases) {
      const row = this.root.createElement("tr");

      const date = this.root.createElement("td");
      date.textContent = formatPlainDate(purchase.purchasedAt);

      const period = this.root.createElement("td");
      period.textContent = purchase.periodDays === 1 ? "1 day" : `${purchase.periodDays} days`;

      const amount = this.root.createElement("td");
      amount.textContent = formatAmount(purchase.amountCents, purchase.currency);

      const invoice = this.root.createElement("td");
      const invoiceButton = this.root.createElement("button");
      invoiceButton.type = "button";
      invoiceButton.className = "linkbtn profile-history__invoice";
      invoiceButton.innerHTML = '<i class="fa-solid fa-file-pdf"></i> Download invoice';
      invoiceButton.addEventListener("click", () => void this.downloadInvoice(purchase.id, invoiceButton));
      invoice.append(invoiceButton);

      row.append(date, period, amount, invoice);
      this.historyBody.append(row);
    }
  }

  private async downloadInvoice(purchaseId: number, button: HTMLButtonElement): Promise<void> {
    button.disabled = true;
    try {
      const blob = await this.api.downloadInvoice(purchaseId);
      const url = URL.createObjectURL(blob);
      const link = this.root.createElement("a");
      link.href = url;
      link.download = `invoice-${purchaseId}.pdf`;
      link.click();
      // Deferred, since revoking immediately can beat the download starting
      // in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      this.historyNotice.textContent =
        error instanceof ApiError ? error.message : "The invoice could not be downloaded.";
      this.historyNotice.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  private showProfileNotice(message: string | null): void {
    this.profileNotice.textContent = message ?? "";
    this.profileNotice.hidden = message === null;
  }

  private showPasswordNotice(message: string | null): void {
    this.passwordNotice.textContent = message ?? "";
    this.passwordNotice.hidden = message === null;
  }

  private showRedeemNotice(message: string | null): void {
    this.redeemNotice.textContent = message ?? "";
    this.redeemNotice.hidden = message === null;
  }

  /** Escape and Tab, the two things a native `<dialog>` handles for free. */
  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      this.card.querySelectorAll<HTMLElement>('button, input:not([disabled]), select, a[href], [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    if (event.shiftKey && this.root.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && this.root.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  private setBackgroundInert(inert: boolean): void {
    for (const child of Array.from(this.root.body.children)) {
      if (child !== this.overlay) (child as HTMLElement).inert = inert;
    }
  }

  private close(): void {
    this.overlay.hidden = true;
    this.setBackgroundInert(false);
    this.root.removeEventListener("keydown", this.handleKeydown, true);
    this.previouslyFocused?.focus();
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.();
  }
}

/** A plain date, with no "Expires"/"Expired" framing — used for both plan dates here. */
function formatPlainDate(iso: string | null | undefined): string {
  if (iso == null) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** An amount in minor units, or "—" for a comped purchase with nothing recorded. */
function formatAmount(amountCents: number | null, currency: string): string {
  if (amountCents == null) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amountCents / 100);
}
